import * as THREE from 'three';
import Experience from "../Experience";

export default class Rain {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.resources = this.experience.resources;

        // --- RESSURSER ---
        // terrengDråpe: brukes til splashes (modell)
        // drop: tekstur for regn
        // snowflake: tekstur for snø
        this.terrainDropResource = this.resources.items.terrengDråpe; // GLTF / modell
        this.dropTexture = this.resources.items.drop;                 // Texture
        this.snowTexture = this.resources.items.snowflake;            // Texture

        // --- VÆR-STATE ---
        this.mode = "rain";       // "clear" | "rain" | "snow"
        this.intensity = 1.0;     // 0..1 (brukes på opacity nå, kan senere brukes til tetthet)
        this.windDirectionDeg = 0; // 0 = +X, 90 = +Z
        this.windSpeed = 0;        // horisontal m/s-ish følelse (justert i update)
        this.wind = new THREE.Vector3(0, 0, 0);

        // Fallhastigheter (juster etter smak)
        this.baseRainSpeed = 3.0; // regn
        this.baseSnowSpeed = 0.25; // snø

        this.setRainGeometry();
        this.setRainMaterial();
        this.setRainMesh();
        // this.setSplashModel(); // terr3engDråpe (kan brukes senere til splashes)

        this._initHotkeys();
    }

    // -------------------------------------------------
    // MODEL: Terreng-dråpe (til splashes senere)
    // -------------------------------------------------
    setSplashModel() {
        if (!this.terrainDropResource || !this.terrainDropResource.scene) return;

        this.splashModel = this.terrainDropResource.scene;
        this.scene.add(this.splashModel);

        this.splashModel.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });

        // TODO: senere kan du instansere/cloner denne modellen der dråpene treffer bakken
    }

    // -------------------------------------------------
    // GEOMETRI
    // -------------------------------------------------
    setRainGeometry() {
        this.rainCount = 0; // Antall dråper
        this.rainGeometry = new THREE.BufferGeometry();

        const positions = [];
        const velocities = [];

        // Vi lager et volum rundt (0,0,0) nå – du kan senere flytte rainMesh til kamera-posisjon
        for (let i = 0; i < this.rainCount; i++) {
            positions.push(
                Math.random() * 1000 - 500, // x
                Math.random() * 500,        // y (høyde)
                Math.random() * 1000 - 500  // z
            );
            // velocity blir en "random factor" for fart
            velocities.push(Math.random() * 0.5 + 0.5); 
        }

        this.rainGeometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        );
        this.rainGeometry.setAttribute(
            'velocity',
            new THREE.Float32BufferAttribute(velocities, 1)
        );
    }

    // -------------------------------------------------
    // MATERIAL
    // -------------------------------------------------
    setRainMaterial() {
        this.rainMaterial = new THREE.PointsMaterial({
            map: this.dropTexture,     // starter som regn
            color: 0xffffff,
            size: 0.75,                   // juster – fungerer med tekstur (world units)
            transparent: true,
            opacity: 0.9,
            alphaTest: 0.0001,
            // depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            blending: THREE.AdditiveBlending
        });

        // Viktig for at tekstur med alpha skal funke skikkelig
        this.rainMaterial.needsUpdate = true;
    }

    // -------------------------------------------------
    // MESH
    // -------------------------------------------------
    setRainMesh() {
        this.rainMesh = new THREE.Points(this.rainGeometry, this.rainMaterial);
        this.rainMesh.frustumCulled = false; // ikke cull hvis den følger kamera senere
        this.scene.add(this.rainMesh);
    }

    // -------------------------------------------------
    // VÆR-KONTROLL (API-vennlig senere)
    // -------------------------------------------------
    setMode(mode) {
        this.mode = mode; // "clear" | "rain" | "snow"

        if (mode === "rain") {
            this.rainMaterial.map = this.dropTexture;
            this.rainMaterial.size = 0.75;
            this.rainMaterial.opacity = 0.9 * this.intensity;
            this.rainMaterial.needsUpdate = true;
        } else if (mode === "snow") {
            this.rainMaterial.map = this.snowTexture;
            this.rainMaterial.size = 2.033; // større flakes
            this.rainMaterial.opacity = 0.9 * this.intensity;
            this.rainMaterial.needsUpdate = true;
        } else if (mode === "clear") {
            // Sol: ingen nedbør
            this.rainMaterial.opacity = 0.0;
        }
    }

    setIntensity(value) {
        this.intensity = THREE.MathUtils.clamp(value, 0, 1);
        // foreløpig: bare skaler opacity
        if (this.mode === "clear") {
            this.rainMaterial.opacity = 0.0;
        } else {
            const base = (this.mode === "snow") ? 0.9 : 0.8;
            this.rainMaterial.opacity = base * this.intensity;
        }
    }

    setWind(speed, directionDeg) {
        this.windSpeed = speed;
        this.windDirectionDeg = directionDeg;

        const rad = THREE.MathUtils.degToRad(directionDeg);
        // Vindvektor i XZ
        this.wind.set(
            Math.cos(rad) * speed,
            0,
            Math.sin(rad) * speed
        );
    }

    // Praktisk når du får data fra et vær-API senere:
    setFromApi({ mode, intensity, windSpeed, windDirectionDeg }) {
        if (mode !== undefined) this.setMode(mode);
        if (intensity !== undefined) this.setIntensity(intensity);
        if (windSpeed !== undefined || windDirectionDeg !== undefined) {
            this.setWind(
                windSpeed !== undefined ? windSpeed : this.windSpeed,
                windDirectionDeg !== undefined ? windDirectionDeg : this.windDirectionDeg
            );
        }
    }

    // -------------------------------------------------
    // HOTKEYS (for testing)
    // -------------------------------------------------
    _initHotkeys() {
        this._onKeyDown = this._onKeyDown.bind(this);
        window.addEventListener("keydown", this._onKeyDown);
    }

    _onKeyDown(e) {
        switch (e.key) {
            case "1": // klarvær
                this.setMode("clear");
                console.log("Weather: clear");
                break;
            case "2": // regn
                this.setMode("rain");
                console.log("Weather: rain");
                break;
            case "3": // snø
                this.setMode("snow");
                console.log("Weather: snow");
                break;
            case "+": // mer intensitet
            case "=":
                this.setIntensity(this.intensity + 0.05);
                console.log("Intensity:", this.intensity.toFixed(2));
                break;
            case "-": // mindre intensitet
            case "_":
                this.setIntensity(this.intensity - 0.05);
                console.log("Intensity:", this.intensity.toFixed(2));
                break;
            case "ArrowUp": // øk vindstyrke
                this.setWind(this.windSpeed + 1, this.windDirectionDeg);
                console.log("Wind speed:", this.windSpeed.toFixed(1));
                break;
            case "ArrowDown": // mindre vindstyrke
                this.setWind(Math.max(this.windSpeed - 1, 0), this.windDirectionDeg);
                console.log("Wind speed:", this.windSpeed.toFixed(1));
                break;
            case "ArrowLeft": // roter vindretning
                this.setWind(this.windSpeed, (this.windDirectionDeg - 10 + 360) % 360);
                console.log("Wind dir:", this.windDirectionDeg.toFixed(0));
                break;
            case "ArrowRight":
                this.setWind(this.windSpeed, (this.windDirectionDeg + 10) % 360);
                console.log("Wind dir:", this.windDirectionDeg.toFixed(0));
                break;
        }
    }

    // -------------------------------------------------
    // UPDATE
    // -------------------------------------------------
    update() {
        const positions = this.rainGeometry.attributes.position.array;
        const velocities = this.rainGeometry.attributes.velocity.array;

        // Bestem fall-hastighet ut fra mode
        let fallBase = 0;
        if (this.mode === "rain") fallBase = this.baseRainSpeed;
        if (this.mode === "snow") fallBase = this.baseSnowSpeed;

        for (let i = 0; i < this.rainCount; i++) {
            const idx = i * 3;
            const vxFactor = velocities[i];

            // Hvis klarvær, hopp over oppdatering (partikler er usynlige uansett)
            if (this.mode === "clear") continue;

            // Fall nedover
            positions[idx + 1] -= fallBase * vxFactor;

            // Vind-vinkel: horisontal bevegelse
            // juster 0.1 for å tune hvor mye vind påvirker
            positions[idx + 0] += this.wind.x * 0.1 * vxFactor;
            positions[idx + 2] += this.wind.z * 0.1 * vxFactor;

            // Reset når dråpen/snøfnugget treffer "bakken"
            // (her bruker vi y < 0 som bakkenivå)
            if (positions[idx + 1] < 0) {
                // Her kan du trigge terrengDråpe-splash senere
                // this.spawnSplash(positions[idx], positions[idx + 2]);

                positions[idx + 1] = Math.random() * 500;            // ny høyde
                positions[idx + 0] = Math.random() * 1000 - 500;     // ny x
                positions[idx + 2] = Math.random() * 1000 - 500;     // ny z
            }
        }

        this.rainGeometry.attributes.position.needsUpdate = true;
    }

    // valgfritt senere:
    // spawnSplash(x, z) {
    //     // lag/aktiver en splash på bakken ved (x, z)
    // }
}